/* Generic searchable typeahead for the /elcapp/newquote island --
   extracted from newquote.js. Prop-driven (value is the selected `code`).
   `formatSelected` controls the picked-row text; `render` styles dropdown
   rows. */
import { useState, useEffect, useMemo, C } from '../shared/ui.js';

const TypeAhead = ({ label, value, onChange, options, placeholder, render, formatSelected, icon = null, highlight = false }) => {
  const [q, setQ] = useState('');
  const [focused, setFocused] = useState(false);
  const ref = React.useRef(null);

  const selected = options.find(o => o.code === value);
  const displayText = selected
    ? (formatSelected ? formatSelected(selected) : `${selected.code} — ${selected.name}`)
    : '';
  const open = focused && q.length > 0;

  const filtered = useMemo(() => {
    if (!q) return [];
    const lq = q.toLowerCase();
    return options.filter(o =>
      (o.code || '').toLowerCase().includes(lq) ||
      (o.name || '').toLowerCase().includes(lq) ||
      (o.locode || '').toLowerCase().includes(lq) ||
      (o.unloco || '').toLowerCase().includes(lq)
    ).slice(0, 50);
  }, [options, q]);

  useEffect(() => {
    if (!focused) return;
    const h = e => { if (ref.current && !ref.current.contains(e.target)) { setQ(''); setFocused(false); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [focused]);

  const pick = code => { onChange(code); setQ(''); setFocused(false); };

  const Icon = icon;
  return (
    <div style={{ position: 'relative' }} ref={ref}>
      {label && (Icon
        ? <div className="lbl-rich"><span className="lbl-rich-ic"><Icon sz={15} /></span>{label}</div>
        : <span className="lbl">{label}</span>)}
      <div style={{ position: 'relative' }}>
        <input value={focused ? q : displayText}
               placeholder={placeholder || 'Type to search…'}
               onFocus={() => { setQ(''); setFocused(true); }}
               onChange={e => setQ(e.target.value)}
               style={{ paddingRight: 24,
                        borderColor: highlight ? '#d97706' : (open ? C.blue : undefined),
                        boxShadow: highlight ? '0 0 0 3px rgba(217,119,6,.18)' : undefined }} />
        <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 10, color: C.textD, pointerEvents: 'none' }}>▼</span>
      </div>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200, background: '#fff',
                     border: `1px solid ${C.blue}`, borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,.12)',
                     maxHeight: 260, overflowY: 'auto', marginTop: 2 }}>
          {filtered.length === 0
            ? <div style={{ padding: '10px 12px', fontSize: 12, color: C.textD }}>No matches</div>
            : filtered.map(o => (
                <div key={o.code}
                     onMouseDown={() => pick(o.code)}
                     style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 12,
                             background: o.code === value ? 'rgba(37,99,235,.08)' : 'transparent',
                             borderBottom: `1px solid ${C.brd}` }}>
                  {render ? render(o) : (
                    <>
                      <span className="mono bc" style={{ fontSize: 13, fontWeight: 700, color: C.blue, marginRight: 8 }}>{o.locode || o.code}</span>
                      <span style={{ color: C.text }}>{o.name}</span>
                    </>
                  )}
                </div>
              ))}
        </div>
      )}
    </div>
  );
};

// Segmented on/off-style picker for units (KG|LBS, CM|INCH, EACH|TOTAL) —
// shared by New Quote and the SmartQuote review modal.
const UnitToggle = ({ value, onChange, options }) => (
  <div style={{ display: 'flex', background: C.s3, borderRadius: 6, padding: 2, flexShrink: 0 }}>
    {options.map(o => (
      <button key={o} type="button" onClick={() => onChange(o)}
              style={{ padding: '4px 12px', borderRadius: 5, border: 'none', cursor: 'pointer',
                       fontSize: 11, fontWeight: 700, fontFamily: 'Barlow Condensed,sans-serif',
                       letterSpacing: '.06em',
                       background: value === o ? C.blue : 'transparent',
                       color: value === o ? '#fff' : C.textD,
                       transition: 'all .15s' }}>{o}</button>
    ))}
  </div>
);

export { TypeAhead, UnitToggle };
